import { reactive } from 'vue';

import { getCookie, setCookie, deleteCookie } from '@js/src/cookies';

/*
 * Single source of truth for the enquiry date selection, shared across the
 * property app's sibling components:
 *   - the inline "Desired dates" inputs in EnquiryForm,
 *   - the Availability calendar (now a tap-to-fill picker),
 *   - the PropertyPriceChecker panel (read-only summary + live price).
 *
 * It is mirrored to the existing enquiry_start_date / enquiry_end_date cookies
 * so cross-property prefill AND the unchanged submit payload keep working
 * exactly as before — the cookies remain the persistence layer; this store is
 * just the in-page reactive bridge between components that used to only talk
 * through those cookies.
 */
export const enquiryDates = reactive({
    from: getCookie('enquiry_start_date') || '',
    to: getCookie('enquiry_end_date') || '',
});

/**
 * Set the selection from any control. Auto-corrects a reversed range by
 * swapping (so "to before from" can never reach the summary or payload), then
 * mirrors the result to the cookies.
 */
export function setEnquiryDates(from, to) {
    let start = from || '';
    let end = to || '';

    // Blocking rule "to must be after from" is satisfied by swapping rather than
    // rejecting — the user's two taps still produce a valid range.
    if (start && end && end < start) {
        [start, end] = [end, start];
    }

    enquiryDates.from = start;
    enquiryDates.to = end;

    if (start) {
        setCookie('enquiry_start_date', start);
    } else {
        deleteCookie('enquiry_start_date');
    }

    if (end) {
        setCookie('enquiry_end_date', end);
    } else {
        deleteCookie('enquiry_end_date');
    }
}

/** Nights in a stay: check-in inclusive, checkout exclusive. 0 for an invalid range. */
export function nightsBetween(from, to) {
    if (!from || !to) return 0;

    const a = new Date(`${from}T00:00:00`);
    const b = new Date(`${to}T00:00:00`);
    const ms = b - a;

    if (Number.isNaN(ms) || ms <= 0) return 0;

    return Math.round(ms / 86400000);
}

/** The Y-m-d nights a stay occupies (check-in inclusive, checkout exclusive). */
export function windowNights(from, to) {
    const out = [];
    const n = nightsBetween(from, to);
    if (!n) return out;

    const d = new Date(`${from}T00:00:00`);
    for (let i = 0; i < n; i++) {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        out.push(`${y}-${m}-${day}`);
        d.setDate(d.getDate() + 1);
    }

    return out;
}

/**
 * Human-readable summary — the mis-entry guard. ALWAYS spells out weekday,
 * month name and year so a wrong-month/wrong-year pick is obvious:
 *   "Fri 27 – Sun 29 November 2026 · 2 nights"
 * Collapses the repeated month/year on the left side when both dates share them.
 */
export function formatDateRangeSummary(from, to) {
    if (!from || !to) return '';

    const a = new Date(`${from}T00:00:00`);
    const b = new Date(`${to}T00:00:00`);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return '';

    const wd = (d) => d.toLocaleDateString('en-GB', { weekday: 'short' });
    const day = (d) => d.getDate();
    const month = (d) => d.toLocaleDateString('en-GB', { month: 'long' });
    const year = (d) => d.getFullYear();

    const sameMonthYear = a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
    const sameYear = a.getFullYear() === b.getFullYear();

    let left;
    if (sameMonthYear) {
        left = `${wd(a)} ${day(a)}`;
    } else if (sameYear) {
        left = `${wd(a)} ${day(a)} ${month(a)}`;
    } else {
        left = `${wd(a)} ${day(a)} ${month(a)} ${year(a)}`;
    }

    const right = `${wd(b)} ${day(b)} ${month(b)} ${year(b)}`;

    const n = nightsBetween(from, to);
    const nights = `${n} night${n === 1 ? '' : 's'}`;

    return `${left} – ${right} · ${nights}`;
}

/**
 * Availability of a selected range against the property's available dates.
 * Returns 'available' (every night available), 'unavailable' (ANY night not
 * available), or null (incomplete range / no availability data to check). Shared
 * by the form's nights line + warning and the calendar's green/amber colouring.
 */
export function rangeAvailability(from, to, availableDates) {
    if (!from || !to || !availableDates || !availableDates.length) return null;

    const nights = windowNights(from, to);
    if (!nights.length) return null;

    const available = new Set(availableDates);

    return nights.every((night) => available.has(night)) ? 'available' : 'unavailable';
}

/**
 * Nearest date windows at the SAME property — EITHER SIDE of the requested
 * check-in — that match the requested stay's shape (same number of nights AND
 * same check-in weekday, so a weekend suggests weekends and a week suggests
 * weeks) and are fully available.
 *
 * Lets a guest whose dates are booked shift to dates the owner can actually
 * take (keeping the enquiry on this house). Scans `weeksEitherSide` weeks before
 * and after the requested week; because the weekday is fixed, candidates fall on
 * whole-week offsets. Returns up to `limit` windows as { startDate, endDate,
 * nights }, nearest to the requested date first (ties: earlier date). Windows in
 * the past naturally drop out — availableDates only holds from-today dates.
 * Purely client-side — driven by the availableDates the form already holds.
 */
export function sameShapeAvailableWindows(from, to, availableDates, limit = 3, weeksEitherSide = 8) {
    const nights = nightsBetween(from, to);
    if (!nights || !availableDates || !availableDates.length) return [];

    const start = new Date(`${from}T00:00:00`);
    if (Number.isNaN(start.getTime())) return [];

    const available = new Set(availableDates);

    const iso = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    const found = [];

    // Whole-week offsets keep the same check-in weekday. w === 0 is the requested
    // (booked) week, so it's skipped.
    for (let w = -weeksEitherSide; w <= weeksEitherSide; w++) {
        if (w === 0) continue;

        const cursor = new Date(start);
        cursor.setDate(cursor.getDate() + w * 7);

        const day = new Date(cursor);
        let ok = true;
        for (let n = 0; n < nights; n++) {
            if (!available.has(iso(day))) {
                ok = false;
                break;
            }
            day.setDate(day.getDate() + 1);
        }

        if (ok) {
            // `day` has advanced `nights` days from the check-in → the checkout.
            found.push({ startDate: iso(cursor), endDate: iso(day), nights, distance: Math.abs(w) });
        }
    }

    // Nearest to the requested date first; on a tie (same distance either side),
    // the earlier date wins for a stable, predictable order.
    found.sort((a, b) => a.distance - b.distance || (a.startDate < b.startDate ? -1 : 1));

    const out = found.slice(0, limit).map(({ distance, ...window }) => window);

    return out;
}

/** ISO Y-m-d → UK display "Sat 17 Oct 2026" (mirrors Flatpickr altFormat "D j M Y"). */
export function formatUkDate(iso) {
    if (!iso) return '';

    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return '';

    const wd = d.toLocaleDateString('en-GB', { weekday: 'short' });
    const mon = d.toLocaleDateString('en-GB', { month: 'short' });

    return `${wd} ${d.getDate()} ${mon} ${d.getFullYear()}`;
}

/** Compact range for the non-blocking mobile confirmation: "27–29 Nov". */
export function formatShortRange(from, to) {
    if (!from || !to) return '';

    const a = new Date(`${from}T00:00:00`);
    const b = new Date(`${to}T00:00:00`);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return '';

    const day = (d) => d.getDate();
    const mon = (d) => d.toLocaleDateString('en-GB', { month: 'short' });

    if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) {
        return `${day(a)}–${day(b)} ${mon(b)}`;
    }

    return `${day(a)} ${mon(a)} – ${day(b)} ${mon(b)}`;
}
